import { useEffect, useState } from 'react'
import { createRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useAuthStore } from '@/application/stores/auth.store'
import { useGetAllInvestigations } from '@/application/services/investigation.service'
import { useGetLeadGroups } from '@/application/services/lead.service'
import { managementLayoutRoute } from '@/ui/pages/management'
import { ActiveContextSection } from './components/ActiveContextSection'
import { BotPanelSection } from './components/BotPanelSection'
import { InvestigationsAccordionSection } from './components/InvestigationsAccordionSection'
import { LiveHeader, type LiveSortOrder } from './components/LiveHeader'
import { LiveStatsSection } from './components/LiveStatsSection'

// ──────────────────────────────────────────────
// Route — /manage/live
// ──────────────────────────────────────────────
export const liveRoute = createRoute({
  getParentRoute: () => managementLayoutRoute,
  path: 'live',
  validateSearch: (search: Record<string, unknown>) => ({
    twitch: typeof search['twitch'] === 'string' ? search['twitch'] : undefined,
    bot: typeof search['bot'] === 'string' ? search['bot'] : undefined,
    reason: typeof search['reason'] === 'string' ? search['reason'] : undefined,
  }),
  beforeLoad: () => {
    if (import.meta.env.VITE_TWITCH_ENABLE !== 'true') {
      throw redirect({ to: '/manage/dashboard' })
    }
    const { user } = useAuthStore.getState()
    // Admins are web-only; they have no Twitch channel and cannot access the Live section.
    if (user && user.role !== 'streamer' && user.role !== 'moderator') {
      throw redirect({ to: '/manage/dashboard' })
    }
  },
  component: LivePage,
})

// ──────────────────────────────────────────────
// Page composition
// ──────────────────────────────────────────────
export function LivePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const search = useSearch({ strict: false })

  // Page-level state shared between the header (controls) and the accordion (display)
  const [sortOrder, setSortOrder] = useState<LiveSortOrder>('newest')
  const [autoRefresh, setAutoRefresh] = useState(true)

  // Refetch handles for the manual refresh button + auto-refresh interval.
  // React Query dedupes — the same hooks called inside child sections share
  // the same cache entries, so this triggers refetches everywhere.
  const { refetch: refetchTopics } = useGetAllInvestigations()
  const { refetch: refetchLeads } = useGetLeadGroups()

  // ─── Twitch OAuth callback handling ───────────────────────────────────────
  useEffect(() => {
    if (search.twitch === 'connected') {
      toast.success(
        search.bot
          ? t('admin.twitch_connected_toast', { username: search.bot })
          : t('admin.twitch_connected_generic'),
      )
      navigate({
        to: '/manage/live',
        search: { twitch: undefined, bot: undefined, reason: undefined },
        replace: true,
      })
    } else if (search.twitch === 'denied') {
      toast.error(t('admin.twitch_denied'))
      navigate({
        to: '/manage/live',
        search: { twitch: undefined, bot: undefined, reason: undefined },
        replace: true,
      })
    } else if (search.twitch === 'error') {
      toast.error(`OAuth error: ${search.reason ?? 'unknown'}`)
      navigate({
        to: '/manage/live',
        search: { twitch: undefined, bot: undefined, reason: undefined },
        replace: true,
      })
    }
  }, [search, navigate, t])

  // ─── Auto-refresh every 30 s ──────────────────────────────────────────────
  useEffect(() => {
    if (!autoRefresh) return
    const interval = setInterval(() => {
      refetchLeads()
    }, 30_000)
    return () => clearInterval(interval)
  }, [autoRefresh, refetchLeads])

  const handleRefresh = async () => {
    try {
      await Promise.all([refetchTopics(), refetchLeads()])
      toast.success(t('admin.live_refreshed'))
    } catch {
      toast.error(t('admin.live_refresh_failed'))
    }
  }

  return (
    <div className="space-y-6">
      <LiveHeader
        sortOrder={sortOrder}
        onSortOrderChange={setSortOrder}
        autoRefresh={autoRefresh}
        onAutoRefreshToggle={() => setAutoRefresh((prev) => !prev)}
        onRefresh={handleRefresh}
      />
      <BotPanelSection />
      <ActiveContextSection />
      <LiveStatsSection />
      <InvestigationsAccordionSection sortOrder={sortOrder} />
    </div>
  )
}
